/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import {
    ControlType,
    ISettingGroupDefinition,
    Levels,
    SectionHeading,
    SettingGroupDefinition,
    StringResource
} from "@nuance/hierarchical-settings-ui-domain";
import {
    IFieldDefinitionPanelSectionHeadingData,
    IFieldDefinitionPanelSectionHeadingProps,
    IFieldDefinitionsPanelData,
    IFieldDefinitionsPanelProps
} from "../Panels";
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IDomainDataMapper } from "./IDomainDataMapper";

const logger: ILogger = getLogger();

/**
 * Maps section heading data from the setting group definition
 * to/from a section heading panel object.
 */
export class DomainDataMapperSectionHeading implements IDomainDataMapper {
    /**
     * Control type expected by this mapper
     */
    readonly RequiredControlType = ControlType.SectionHeading;

    /**
     * Setting group definition object
     */
    readonly definition: ISettingGroupDefinition;

    /**
     * Constructor
     *
     * @param definition Setting group definition
     */
    constructor(definition: ISettingGroupDefinition) {
        this.definition = definition;
    }

    /**
     * Builds a section heading panel object for adding a new control.
     *
     * @returns Section heading panel data
     */
    BuildPanelPropsForNewItem = (): IFieldDefinitionsPanelProps => {
        const result: IFieldDefinitionPanelSectionHeadingProps = {
            common: {
                panelTitle: "Add Section",
                originalSettingName: "",
                isNew: true,
                isOpen: true
            },
            data: {
                definitionType: ControlType.SectionHeading,
                sectionHeadingId: "",
                heading: "",
                accessLevels: Levels.Classic,
                visibleRule: ""
            }
        };

        return result;
    };

    /**
     * Builds a section heading panel data object for an existing
     * section heading control.
     *
     * @param ctrlGuid Control GUID for the section heading
     * @returns Section heading panel data object
     */
    BuildPanelPropsForExistingItem = (ctrlGuid: string): IFieldDefinitionsPanelProps => {
        const sg = new SettingGroupDefinition(this.definition);
        const sh = this.findControl(sg, ctrlGuid);
        const stringResources = sg.UI.StringResources;
        const sr = new StringResource(sh.TitleStringId);

        const result: IFieldDefinitionPanelSectionHeadingProps = {
            common: {
                panelTitle: "Edit Section",
                originalSettingName: sh.SectionHeadingId,
                isNew: false,
                isOpen: true
            },
            data: {
                ctrlGuid,
                definitionType: ControlType.SectionHeading,
                sectionHeadingId: sh.SectionHeadingId,
                heading: stringResources.get(sr.MapKey)?.Text ?? "",
                accessLevels: sh.AccessLevel,
                visibleRule: sh.VisibleRuleName
            }
        };

        return result;
    };

    /**
     * Saves the section heading panel data to a new setting group definition.
     *
     * @param data Section heading panel data
     * @returns Setting group definition object
     */
    SaveDataToDefinition = (data: IFieldDefinitionsPanelData): ISettingGroupDefinition => {
        if (data.definitionType !== ControlType.SectionHeading) {
            logger.logError("Invalid control type", { type: ControlType[data.definitionType] });
            throw new Error("Invalid definition type.");
        }

        const shd = data as IFieldDefinitionPanelSectionHeadingData;
        const sg = new SettingGroupDefinition(this.definition);

        if (data.ctrlGuid === undefined) {
            this.addNewSectionHeading(shd, sg);
        } else {
            this.updateExistingSectionHeading(data.ctrlGuid, shd, sg);
        }

        return sg.toISettingGroupDefinition();
    };

    /**
     * Adds a new section to the setting definition
     *
     * @param shd Panel heading data
     * @param sg Setting group definition
     */
    private addNewSectionHeading(
        shd: IFieldDefinitionPanelSectionHeadingData,
        sg: SettingGroupDefinition
    ): void {
        const h = sg.UI.AddSectionHeading(shd.sectionHeadingId);
        h.AccessLevel = shd.accessLevels;
        h.VisibleRuleName = shd.visibleRule;

        const sr = new StringResource(h.TitleStringId);
        sr.Text = shd.heading;
        sg.UI.StringResources.set(sr.MapKey, sr);
    }

    /**
     * Updates an existing section heading in the setting definition
     *
     * @param shd Panel section heading data
     * @param sg Setting group definition
     */
    private updateExistingSectionHeading(
        ctrlGuid: string,
        shd: IFieldDefinitionPanelSectionHeadingData,
        sg: SettingGroupDefinition
    ): void {
        const sh = this.findControl(sg, ctrlGuid);

        // user not allowed to change title string id
        if (sh.SectionHeadingId !== shd.sectionHeadingId) {
            logger.logError("Section id changed", {
                oldId: sh.SectionHeadingId,
                newId: shd.sectionHeadingId
            });
            throw new Error("Section id can not change.");
        }

        sh.AccessLevel = shd.accessLevels;
        sh.VisibleRuleName = shd.visibleRule;

        const sr = new StringResource(sh.TitleStringId);
        sr.Text = shd.heading;
        sg.UI.StringResources.set(sr.MapKey, sr);
    }

    /**
     * Find the control and field definition for a
     * control GUID
     *
     * @param sg Setting group definition
     * @param ctrlGuid Control GUID
     * @returns TextFormGroup and StringFieldDefinition
     * associated with the control
     */
    private findControl = (sg: SettingGroupDefinition, ctrlGuid: string): SectionHeading => {
        const ctrlIndex = sg.UI.Controls.findIndex(i => i.CtrlGuid == ctrlGuid);
        if (ctrlIndex === -1) {
            logger.logError("Unable to find control", { ctrlGuid });
            throw new Error("Unable to find control.");
        }

        const uiCtrl = sg.UI.Controls[ctrlIndex];
        if (uiCtrl.ControlDiscriminator !== this.RequiredControlType) {
            logger.logError("Control type is not text", {
                type: ControlType[uiCtrl.ControlDiscriminator]
            });
            throw new Error("Invalid control type.  Expecting SectionHeadingField control.");
        }

        return uiCtrl as SectionHeading;
    };
}
